import React from "react";

interface QuestionNavigationButtonsProps {
  currentQuestionIndex: number;
  questionsLength: number;
  isQuizActive: boolean;
  isSavingAnswer: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onSkip: () => void;
  onSubmit: () => void;
}

const QuestionNavigationButtons: React.FC<QuestionNavigationButtonsProps> = ({
  currentQuestionIndex,
  questionsLength,
  isQuizActive,
  isSavingAnswer,
  onPrevious,
  onNext,
  onSkip,
  onSubmit,
}) => (
  <>
    {/* Desktop Sticky Bottom Navigation */}
    <div className="d-none d-md-block position-fixed bottom-0 start-0 w-100 bg-white border-top shadow-lg p-3" style={{ zIndex: 1040 }}>
      <div className="container">
        <div className="row">
          {/* Left side buttons */}
          <div className="col-6">
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary px-4"
                style={{ borderRadius: '25px' }}
                onClick={onPrevious}
                disabled={currentQuestionIndex === 0 || !isQuizActive || isSavingAnswer}
              >
                {isSavingAnswer ? (
                  <>
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                      <span className="visually-hidden">Saving...</span>
                    </div>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-chevron-left me-2"></i>
                    Previous
                  </>
                )}
              </button>
              <button
                className="btn btn-outline-warning px-4"
                style={{ borderRadius: '25px' }}
                onClick={onSkip}
                disabled={currentQuestionIndex === questionsLength - 1 || !isQuizActive || isSavingAnswer}
              >
                {isSavingAnswer ? (
                  <>
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                      <span className="visually-hidden">Saving...</span>
                    </div>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-forward me-2"></i>
                    Skip
                  </>
                )}
              </button>
            </div>
          </div>
          
          {/* Right side buttons */}
          <div className="col-6">
            <div className="d-flex gap-2 justify-content-end">
              {currentQuestionIndex === questionsLength - 1 ? (
                <button
                  className="btn btn-success px-5"
                  style={{ borderRadius: '25px', fontSize: '16px', fontWeight: 'bold' }}
                  onClick={onSubmit}
                  disabled={!isQuizActive || isSavingAnswer}
                >
                  {isSavingAnswer ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Saving...</span>
                      </div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check me-2"></i>
                      Submit Quiz
                    </>
                  )}
                </button>
              ) : (
                <>
                  <button
                    className="btn btn-primary px-4"
                    style={{ borderRadius: '25px' }}
                    onClick={onNext}
                    disabled={currentQuestionIndex === questionsLength - 1 || !isQuizActive || isSavingAnswer}
                  >
                    {isSavingAnswer ? (
                      <>
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Saving...</span>
                        </div>
                        Saving...
                      </>
                    ) : (
                      <>
                        Next
                        <i className="fas fa-chevron-right ms-2"></i>
                      </>
                    )}
                  </button>
                  <button
                    className="btn btn-outline-danger px-4"
                    style={{ borderRadius: '25px' }}
                    onClick={onSubmit}
                    disabled={!isQuizActive || isSavingAnswer}
                  >
                    {isSavingAnswer ? (
                      <>
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Saving...</span>
                        </div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-flag-checkered me-2"></i>
                        Finish Quiz
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Mobile Sticky Bottom Navigation */}
    <div className="d-md-none position-fixed bottom-0 start-0 w-100 bg-white border-top shadow-lg p-3" style={{ zIndex: 1040 }}>
      <div className="container-fluid">
        <div className="row g-2">
          <div className="col-3">
            <button
              className="btn btn-outline-secondary w-100"
              onClick={onPrevious}
              disabled={currentQuestionIndex === 0 || !isQuizActive || isSavingAnswer}
              style={{ fontSize: '0.8rem', minHeight: '48px' }}
            >
              <i className="fas fa-chevron-left d-block"></i>
              <div className="small">Prev</div>
            </button>
          </div>
          <div className="col-3">
            <button
              className="btn btn-outline-warning w-100"
              onClick={onSkip}
              disabled={currentQuestionIndex === questionsLength - 1 || !isQuizActive || isSavingAnswer}
              style={{ fontSize: '0.8rem', minHeight: '48px' }}
            >
              <i className="fas fa-forward d-block"></i>
              <div className="small">Skip</div>
            </button>
          </div>
          <div className="col-3">
            <button
              className="btn btn-primary w-100"
              onClick={onNext}
              disabled={currentQuestionIndex === questionsLength - 1 || !isQuizActive || isSavingAnswer}
              style={{ fontSize: '0.8rem', minHeight: '48px' }}
            >
              <i className="fas fa-chevron-right d-block"></i>
              <div className="small">Next</div>
            </button>
          </div>
          <div className="col-3">
            <button
              className="btn btn-danger w-100"
              onClick={onSubmit}
              disabled={!isQuizActive || isSavingAnswer}
              style={{ fontSize: '0.8rem', minHeight: '48px' }}
            >
              <i className="fas fa-flag-checkered d-block"></i>
              <div className="small">Finish</div>
            </button>
          </div>
        </div>
        {/* Submit Quiz Button for last question on mobile */}
        {currentQuestionIndex === questionsLength - 1 && (
          <div className="mt-2">
            <button
              className="btn btn-success w-100"
              onClick={onSubmit}
              disabled={!isQuizActive || isSavingAnswer}
              style={{ fontSize: '0.9rem' }}
            >
              {isSavingAnswer ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Saving...</span>
                  </div>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-check me-2"></i>
                  Submit Quiz
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  </>
);

export default QuestionNavigationButtons;
